import { Copy, GitMerge, X, RefreshCw } from 'lucide-react';
import { HudButton, HudCard } from '../hud';
import MaterialThumbnail from './MaterialThumbnail';
import { resolveThumbUrl } from '../../api/materialthequeApi';

/**
 * DuplicatesPanel — groupes de doublons détectés (même produit vu dans plusieurs pièces).
 * `items` : dictionnaire material_id -> item du résultat (pour vignettes / noms).
 */
export const DuplicatesPanel = ({ groups, itemsById, onMerge, onRedetect, onClose, busyGroupId, detecting, readOnly }) => {
  if (!groups?.length) return null;
  return (
    <HudCard variant="glass" data-testid="materialtheque-duplicates-panel" style={{ border: '1px solid rgba(217,160,91,0.35)' }}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Copy className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
          <span className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 15 }}>
            {groups.length} doublon{groups.length > 1 ? 's' : ''} potentiel{groups.length > 1 ? 's' : ''}
          </span>
          <span className="text-[12px]" style={{ color: 'var(--c-text-muted)' }}>— même produit repéré dans plusieurs pièces</span>
        </div>
        <div className="flex items-center gap-2">
          <HudButton variant="ghost" size="sm" onClick={onRedetect} disabled={detecting} leftIcon={<RefreshCw className={`w-3.5 h-3.5 ${detecting ? 'animate-spin' : ''}`} />} data-testid="materialtheque-duplicates-redetect">Re-détecter</HudButton>
          <button type="button" onClick={onClose} className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0" data-testid="materialtheque-duplicates-close"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="space-y-2">
        {groups.map((g) => {
          const members = g.ids.map((id) => itemsById[id]).filter(Boolean);
          const keep = itemsById[g.suggested_keep_id] || members[0];
          return (
            <div key={g.group_id} className="flex items-center gap-3 flex-wrap rounded-xl px-3 py-2.5" style={{ background: 'var(--c-surface-1)', border: '1px solid var(--c-border)' }} data-testid={`duplicate-group-${g.group_id}`}>
              <div className="flex items-center gap-2 flex-1 min-w-[260px] flex-wrap">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 rounded-lg px-2 py-1" style={{ background: m.id === keep?.id ? 'rgba(217,160,91,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${m.id === keep?.id ? 'rgba(217,160,91,0.5)' : 'transparent'}` }} title={m.id === keep?.id ? 'Matériau conservé (référence)' : 'Sera fusionné dans la référence'}>
                    <MaterialThumbnail color={m.thumb_color} pattern={m.thumb_pattern} src={resolveThumbUrl(m.thumb)} size={30} rounded={8} label={m.name} />
                    <div className="min-w-0">
                      <p className="text-[12px] truncate max-w-[200px]" style={{ color: 'var(--c-text)' }}>{m.name}</p>
                      <p className="text-[10px]" style={{ color: 'var(--c-text-muted)' }}>{m.section_label || m.room_type || ''}{m.id === keep?.id ? ' · référence' : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] max-w-[220px] truncate" style={{ color: 'var(--c-text-muted)' }} title={g.reason}>{g.reason} · {Math.round((g.confidence || 0) * 100)}%</span>
                {!readOnly && (
                  <HudButton variant="primary" size="sm" onClick={() => onMerge(g)} disabled={!!busyGroupId} leftIcon={<GitMerge className="w-3.5 h-3.5" />} data-testid={`duplicate-merge-${g.group_id}`}>
                    {busyGroupId === g.group_id ? 'Fusion…' : 'Fusionner'}
                  </HudButton>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </HudCard>
  );
};

export default DuplicatesPanel;
